import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { IntakeFormTemplate } from '../../../types/formBuilder';
import { getFormById, saveFormSubmission, getFormSubmission } from '../../../firebase/formManagement';
import { DynamicFormRenderer } from './DynamicFormRenderer';

export function DynamicIntakeFormPage() {
  const { formId, caseId } = useParams<{ formId: string; caseId: string }>();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  
  const [formTemplate, setFormTemplate] = useState<IntakeFormTemplate | null>(null);
  const [initialAnswers, setInitialAnswers] = useState<{ [fieldName: string]: any }>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [user, setUser] = useState<any>(null);

  useEffect(() => {
    // Load user from localStorage
    const savedUser = localStorage.getItem('user');
    if (savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch (err) {
        console.error('Error parsing user:', err);
      }
    }
  }, []);

  useEffect(() => {
    if (formId && caseId) {
      loadFormAndSubmission();
    }
  }, [formId, caseId]);

  const loadFormAndSubmission = async () => {
    if (!formId || !caseId) return;

    setIsLoading(true);
    try {
      // Load form template
      const form = await getFormById(formId);
      if (!form) {
        alert(t('intakeForms.formNotFound'));
        navigate(-1);
        return;
      }
      setFormTemplate(form);

      // Load existing submission if any
      const submission = await getFormSubmission(formId, caseId);
      if (submission) {
        setInitialAnswers(submission.answers || {});
      }
    } catch (error) {
      console.error('Error loading form:', error);
      alert(t('intakeForms.errorLoading'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (answers: { [fieldName: string]: any }) => {
    if (!formId || !caseId || !user) {
      alert(t('intakeForms.missingInfo'));
      return;
    }

    setIsSaving(true);
    try {
      const result = await saveFormSubmission(
        formId,
        caseId,
        answers,
        user.email,
        'completed'
      );

      if (result.success) {
        // Log activity
        const { addActivityNote } = await import('../../../firebase');
        await addActivityNote(
          caseId,
          `${t('intakeForms.completed')}: ${formTemplate?.name}`,
          'completed',
          user.email
        );

        alert(t('intakeForms.saveSuccess'));
        navigate(`/${i18n.language}/patient/${caseId}#intake`);
      } else {
        alert(result.error || t('intakeForms.saveFailed'));
      }
    } catch (error) {
      console.error('Error saving form:', error);
      alert(t('intakeForms.saveFailed'));
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="app">
        <div className="container">
          <div className="login-card">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '18px', color: '#666666' }}>
                {t('common.loading')}...
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!formTemplate) {
    return (
      <div className="app">
        <div className="container">
          <div className="login-card">
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px' }}>❌</div>
              <div style={{ fontSize: '18px', color: '#666666' }}>
                {t('intakeForms.formNotFound')}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const formName = i18n.language === 'he' ? (formTemplate.nameHe || formTemplate.name) : (formTemplate.nameEn || formTemplate.name);

  return (
    <div className="app" style={{ direction: i18n.language === 'he' ? 'rtl' : 'ltr' }}>
      <div className="container">
        <div className="create-patient-container" style={{ maxWidth: '1200px', width: '100%' }}>
          <div className="create-patient-form">
            {/* Header */}
            <div className="login-header">
              <button
                onClick={() => navigate(-1)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#007acc',
                  cursor: 'pointer',
                  fontSize: '14px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  padding: '8px 0',
                  marginBottom: '16px'
                }}
              >
                ← {t('common.back')}
              </button>
              
              <h1 className="form-title" style={{ marginBottom: '8px' }}>
                {formName}
              </h1>
              
              {formTemplate.description && (
                <p style={{ color: '#666', marginBottom: '20px', fontSize: '14px' }}>
                  {formTemplate.description}
                </p>
              )}

              {/* Case Info */}
              <div style={{ 
                background: '#f8f9fa', 
                padding: '12px 16px', 
                borderRadius: '8px',
                marginBottom: '24px',
                fontSize: '13px',
                color: '#666'
              }}>
                {t('patientDetail.caseId')}: <strong>{caseId}</strong>
              </div>
            </div>

            {/* Form Content */}
            <DynamicFormRenderer
              formTemplate={formTemplate}
              initialValues={initialAnswers}
              onSubmit={handleSubmit}
              submitButtonText={isSaving ? t('common.saving') : t('intake.save')}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

export default DynamicIntakeFormPage;

